import { OTHERWORLD_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/otherworldBackdrops';
import type { AtmospherePreset } from '../presets.config';
import { ATMOSPHERE_PALETTE as P } from '../palette';
import { Z_LAYERS } from '../../../game/rendering/Z_LAYERS';

/**
 * Arena / Brawl — the most-watched surfaces (/brawl social-stream hub, /arena
 * PvP testbed). PUNCHED UP (DJ 2026-06-18 "punch it up + reusable"): a torchlit
 * dusk-colosseum. Dramatic sky, warm light shafts raking the pit, a drifting
 * ember field + warm light bed, a distant columned backdrop for real depth, and
 * a strong framing vignette.
 *
 * COMBAT-REACTIVE: the mooded layers (god-rays, light bed, embers, floor glow)
 * swell on combat heat via the CombatMoodDriver (EVENTS.COMBAT_MOOD → multiplied
 * onto the scene mood) and ease back in a lull — so the arena visibly heats up
 * during a fight. The parallax backdrop + vignette are frame kinds (constant).
 * Kept readable: the bright elements sit at the floor / edges / sky, not over
 * the play space.
 */
export const ARENA: AtmospherePreset = {
  id: 'arena',
  layers: [
    // Dramatic dusk-colosseum sky.
    { kind: 'skyGradient', from: 0x1a0b28, to: 0x4a2342 },
    // Low dusk sun sinking behind the colosseum — a focal sky element.
    { kind: 'celestial', atX: 0.76, atY: 0.34, color: 0xffd093, haloColor: 0xff8a44, size: 150, haloScale: 3, coreAlpha: 0.5, haloAlpha: 0.2 },
    // Slow warm dusk clouds drifting high across the sun (sky band, behind the
    // parallax). Soft-light is ADDITIVE, so a warm lit tint reads as glowing
    // sunset haze (a dark tint would add nothing).
    {
      kind: 'cloudBands', count: 4, color: 0xd98f6e,
      sizeMin: 320, sizeMax: 560, alphaMin: 0.06, alphaMax: 0.15,
      driftPxPerSec: 5, bandYMin: 0.05, bandYMax: 0.26,
    },
    // Distant columned backdrop — architectural depth behind the pit (constant frame).
    {
      kind: 'parallax', svg: OTHERWORLD_BACKDROPS.ruins.far, shape: 'columns', color: 0x2a1730, speed: 0.06, y: 0, height: 360, alpha: 0.6,
      depth: Z_LAYERS.PARALLAX_FAR, detail: 1.1, sway: { ampPx: 8, periodMs: 36000 },
    },
    // Warm torch-light shafts raking across the arena (mooded → swell on combat).
    {
      kind: 'godRays',
      count: 4, color: P.amber, widthMin: 70, widthMax: 150, height: 540,
      tiltDeg: 14, periodMinMs: 8000, periodMaxMs: 15000, alphaMin: 0.12, alphaMax: 0.4,
    },
    // Drifting warm light bed for depth (mooded).
    {
      kind: 'lightField',
      count: 16, palette: [P.ember, P.amber, P.torch],
      sizeMin: 90, sizeMax: 220, alphaMin: 0.05, alphaMax: 0.16,
      driftRadius: 46, periodMinMs: 11000, periodMaxMs: 19000,
    },
    // Warm embers wandering up — breathing + combat-reactive.
    {
      kind: 'fireflies',
      count: 22, colors: [P.ember, P.amber, P.torch],
      sizeMin: 5, sizeMax: 12, driftPx: 32,
      periodMinMs: 6000, periodMaxMs: 13000, alphaMin: 0.14, alphaMax: 0.5,
      modulators: [{ target: 'opacity', shape: 'sine', periodMs: 8000, min: 0.7, max: 1.3 }],
    },
    // Floor glow centring the pit, flanking embers, + a cool top counter-glow.
    {
      kind: 'glowBlobs',
      blobs: [
        { color: P.amber, atX: 0.5, atY: 0.97, size: 640, alpha: 0.1 },
        { color: P.ember, atX: 0.2, atY: 0.92, size: 420, alpha: 0.07 },
        { color: P.ember, atX: 0.8, atY: 0.92, size: 420, alpha: 0.07 },
        { color: P.nexusViolet, atX: 0.5, atY: 0.04, size: 520, alpha: 0.06 },
      ],
    },
    // Un-graded and un-vignetted: clean clarity without atmospheric edge rectangles.
  ],
};
